import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DashboardLayout from "../components/DashboardLayout";
import RevenueBackBar from "../components/RevenueBackBar";
import { readAudiencesLink } from "../utils/revenueLinks";
import AudienceChatAnswer from "../components/AudienceChatAnswer";
import {
  api,
  AudienceAgentFinalResult,
  AudienceAgentRun,
  AudienceAttribute,
  AudienceBootstrap,
  AudienceFilter,
  AudienceResultContext,
  AudienceCoverage,
  AudiencePublication,
  AudienceRelationField,
  AudienceInterpretationJob,
  AudienceStatus,
  AudienceSearchResult,
  AudienceVisitor,
  AudienceVisitorActivity,
  AudienceVisitorProfile,
  SavedAudience,
} from "../api/client";
import { useModal } from "../context/ModalContext";
import "../styles/audience-builder.css";

const EMPTY_FILTER: AudienceFilter = { version: 2, all: [] };
const MAX_CONVERSATIONS = 8;
const MAX_MESSAGES = 50;
const SYNC_POLL_MS = 5000;
const PUBLICATION_POLL_MS = 2500;
const INTERPRETATION_POLL_MS = process.env.NODE_ENV === "test" ? 20 : 1000;
const INTERPRETATION_CLIENT_TIMEOUT_MS = 20 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 60;
const COURTRESERVE_GROUP_MEMBER_CAP = 5000;
type ChatMessageKind =
  | "text"
  | "working"
  | "audience_draft"
  | "audience_saved"
  | "error";
type AgentConversationMessage = {
  role: "user" | "assistant";
  text: string;
  resultContext?: AudienceResultContext;
};
interface AudienceDraft {
  name: string;
  sourcePrompt: string;
  filter: AudienceFilter;
  interpretation: string[];
  warnings: string[];
  matchedCount: number;
  eligibleCount: number;
  sourceUpdatedAt?: string | null;
  savedAudienceId?: string | null;
  updatedAt?: string | null;
  requiresSave?: boolean;
}
interface AudienceChatMessage {
  id: string;
  role: "user" | "assistant";
  kind: ChatMessageKind;
  text: string;
  createdAt: string;
  draft?: AudienceDraft;
  retry?: {
    prompt: string;
    filter: AudienceFilter;
    refinement: boolean;
  };
  resultContext?: AudienceResultContext;
}
interface AudienceConversation {
  id: string;
  venueId: string;
  title: string;
  messages: AudienceChatMessage[];
  draft?: AudienceDraft | null;
  createdAt: string;
  updatedAt: string;
  /** Saved-audience views are intentionally transient, not chat history. */
  isSavedView?: boolean;
  /** Informational Q&A is useful in the active session, not in Recent. */
  isInformational?: boolean;
}
interface MemberPreview {
  conversationId: string;
  filterKey: string;
  members: AudienceVisitor[];
  pagination: { page: number; totalPages: number };
  unknownCount: number;
  loading: boolean;
  error?: string | null;
}

const makeId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const interpretationFromAgentResult = (
  result: AudienceAgentFinalResult,
  currentFilter: AudienceFilter,
): NonNullable<AudienceInterpretationJob["result"]> => {
  if (result.type === "failure") {
    const error = new Error(result.displayText || "Audience agent failed");
    error.name = "AudienceAgentFailure";
    throw error;
  }
  const draft = result.draft || null;
  return {
    action:
      result.type === "clarification"
        ? "clarify"
        : result.type === "draft_update"
          ? "audience"
          : "answer",
    message: result.displayText,
    audienceName:
      typeof draft?.audienceName === "string"
        ? draft.audienceName
        : typeof draft?.name === "string"
          ? draft.name
          : "",
    filter:
      draft?.filter && typeof draft.filter === "object"
        ? draft.filter
        : currentFilter,
    interpretation: Array.isArray(draft?.interpretation)
      ? (draft?.interpretation || []).filter((item): item is string => typeof item === "string").slice(0, 5)
      : typeof draft?.summary === "string"
        ? [draft.summary]
        : [],
    proof: [],
    warnings: Array.isArray(draft?.warnings)
      ? (draft?.warnings || []).filter((item): item is string => typeof item === "string").slice(0, 5)
      : [],
    matchedCount:
      typeof draft?.matchedCount === "number" ? draft.matchedCount : undefined,
  };
};
const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
const shortDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString([], { month: "short", day: "numeric" })
    : "—";
const lastRefreshLabel = (value?: string | null) => {
  const refreshedAt = value ? new Date(value) : null;

  return refreshedAt && !Number.isNaN(refreshedAt.getTime())
    ? `Updated ${new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }).format(refreshedAt)}`
    : "Not yet refreshed";
};
const visitorLabel = (count: number) => (count === 1 ? "visitor" : "visitors");
const audienceDefinition = (
  lines: string[] | null | undefined,
  fallback?: string | null,
) => {
  const definition = (lines || [])
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 5);
  if (definition.length) return definition;
  return [fallback?.trim() || "All eligible visitors."];
};
const normalizeSaved = (raw: any): SavedAudience => ({
  id: raw.id,
  name: raw.name,
  description: raw.description,
  sourcePrompt: raw.sourcePrompt ?? raw.source_prompt ?? null,
  filterDefinition:
    raw.filterDefinition || raw.filter_definition || EMPTY_FILTER,
  lastEstimatedCount: raw.lastEstimatedCount ?? raw.last_estimated_count,
  lastEvaluatedAt: raw.lastEvaluatedAt || raw.last_evaluated_at,
  updatedAt: raw.updatedAt || raw.updated_at,
});
const normalizeAttribute = (raw: any): AudienceAttribute => ({
  attributePath: raw.attributePath || raw.attribute_path,
  displayName: raw.displayName || raw.display_name || raw.attributePath,
  dataType: raw.dataType || raw.data_type || "string",
  category: raw.category,
  allowedOperators: raw.allowedOperators || raw.allowed_operators || ["eq"],
  options: raw.options || raw.enum_values,
  coverageCount: raw.coverageCount ?? raw.coverage_count ?? 0,
  coveragePercent: raw.coveragePercent ?? raw.coverage_percent,
  filterEnabled: raw.filterEnabled ?? raw.filter_enabled,
  sensitivity: raw.sensitivity,
});
const formatSyncPhase = (value?: string | null) =>
  (value || "syncing")
    .replaceAll("_", " ")
    .replace(/\bmembers?\b/gi, "visitors");
const scrubText = (value: string) =>
  value.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email removed]");
const audienceBuildError = (error: unknown) => {
  const message = error instanceof Error ? error.message.trim() : "";
  // A failure disposition from the v2 agent has already passed the runtime's
  // strict final-envelope validation. Preserve that bounded, operator-facing
  // explanation while continuing to hide arbitrary transport/provider text.
  if (
    error instanceof Error &&
    error.name === "AudienceAgentFailure" &&
    message &&
    message.length <= 1000
  )
    return scrubText(message);
  // Transport, provider, and parser details are not useful to an operator and
  // should never make it into the conversational UI.
  if (
    !message ||
    /failed to fetch|network(?:\s+error)?|fetch|timeout|timed out|abort|unexpected|syntax|json|request failed|internal server|service unavailable|gateway|econn|cors|provider|model/i.test(
      message,
    )
  )
    return "Something went wrong. Please retry.";
  // Retain a short, actionable business-rule response (for example, an
  // unsupported criterion) while treating unknown server text as technical.
  if (
    message.length <= 180 &&
    /\b(cannot|can’t|can't|must|unsupported|not available|not ready|choose|valid|invalid|requires)\b/i.test(
      message,
    )
  )
    return message;
  return "Something went wrong. Please retry.";
};
const filterHasCriteria = (filter: AudienceFilter): boolean => {
  if (!filter || typeof filter !== "object") return false;
  if ("field" in filter || "relation" in filter || "memberIds" in filter) return true;
  if ("all" in filter) return filter.all.some(filterHasCriteria);
  if ("any" in filter) return filter.any.some(filterHasCriteria);
  if ("not" in filter) return filterHasCriteria(filter.not);
  return false;
};
const canonicalFilterValue = (
  value: unknown,
  parentKey = "",
  parentOperator = "",
): unknown => {
  if (Array.isArray(value)) {
    const normalized = value.map((item) =>
      canonicalFilterValue(item, parentKey, parentOperator),
    );
    const orderDoesNotMatter =
      ["all", "any", "memberIds"].includes(parentKey) ||
      (parentKey === "value" &&
        ["in", "not_in", "contains_any", "contains_all", "contains_none"].includes(
          parentOperator,
        ));
    return orderDoesNotMatter
      ? normalized.sort((left, right) =>
          JSON.stringify(left).localeCompare(JSON.stringify(right)),
        )
      : normalized;
  }
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  const operator =
    typeof record.operator === "string" ? record.operator : parentOperator;
  return Object.fromEntries(
    Object.keys(record)
      .filter((key) => !(parentKey === "" && key === "version"))
      .sort()
      .map((key) => [
        key,
        canonicalFilterValue(record[key], key, operator),
      ]),
  );
};
const semanticFilterKey = (filter: AudienceFilter) =>
  JSON.stringify(canonicalFilterValue(filter));
const noChangeAnswer = (warnings?: string[], refinement = false) => {
  const useful = (warnings || []).find(
    (warning) =>
      !/no change requested|preserv(?:ed|ing).*existing|existing filters? (?:were )?left unchanged/i.test(
        warning,
      ),
  );
  if (refinement)
    return useful && useful.length <= 180
      ? `I didn’t change the audience. ${useful}`
      : "I didn’t change the audience. Tell me the exact criterion to add, remove, or replace.";
  return useful && useful.length <= 220
    ? useful
    : "I couldn’t safely apply that request. Try rephrasing the criteria.";
};
const isPersistableConversation = (conversation: AudienceConversation) =>
  !conversation.isSavedView &&
  conversation.messages.length > 0;
const conversationHistory = (
  messages: AudienceChatMessage[],
): AgentConversationMessage[] =>
  messages
    .filter(
      (message) =>
        message.kind !== "working" &&
        message.kind !== "error" &&
        Boolean(message.text.trim()),
    )
    .slice(-12)
    .map((message) => ({
      role: message.role,
      text: scrubText(message.text),
      ...(message.resultContext ? { resultContext: message.resultContext } : {}),
    }));
const initialConversation = (venueId: string): AudienceConversation => ({
  id: makeId(),
  venueId,
  title: "New audience",
  messages: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  draft: null,
});
const updatedConversation = (
  conversation: AudienceConversation,
  patch: Partial<AudienceConversation>,
): AudienceConversation => ({
  ...conversation,
  ...patch,
  updatedAt: new Date().toISOString(),
});
const audienceSlugCode = (conversationId: string) =>
  conversationId.replace(/[^a-z0-9]/gi, "").toLowerCase().slice(0, 12);
const aiAudienceName = (
  candidate: string | null | undefined,
  conversationId: string,
) => {
  const snippet = (candidate || "Smart audience")
    .replace(/\s*(?:-pc-audience|\(pc-audience-[a-z0-9-]+\))$/i, "")
    .replace(/[.!?]+$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);
  const code = audienceSlugCode(conversationId) || audienceSlugCode(makeId());
  return `${snippet || "Smart audience"} (pc-audience-${code})`;
};
function Sparkle() {
  return <span aria-hidden="true">✦</span>;
}
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

function formatVisitorAttribute(value: unknown, dataType: string, unit?: string | null) {
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" && (dataType === "money" || unit === "USD")) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value / 100);
  }
  if ((dataType === "date" || dataType === "timestamp") && typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        ...(dataType === "timestamp" ? { hour: "numeric", minute: "2-digit" } : {}),
      }).format(parsed);
    }
  }
  if (value && typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export default function AudienceBuilderScreen() {
  const { showAlert, showConfirm } = useModal();
  const [status, setStatus] = useState<AudienceStatus | null>(null);
  const [attributes, setAttributes] = useState<AudienceAttribute[]>([]);
  const [relations, setRelations] = useState<AudienceRelationField[]>([]);
  const [coverage, setCoverage] = useState<AudienceCoverage | null>(null);
  const [saved, setSaved] = useState<SavedAudience[]>([]);
  const [conversations, setConversations] = useState<AudienceConversation[]>(
    [],
  );
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [composer, setComposer] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [manualCopyText, setManualCopyText] = useState<string | null>(null);
  const [memberPreview, setMemberPreview] = useState<MemberPreview | null>(
    null,
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileVisitor, setProfileVisitor] = useState<AudienceVisitor | null>(null);
  const [profile, setProfile] = useState<AudienceVisitorProfile | null>(null);
  const [profileActivity, setProfileActivity] = useState<AudienceVisitorActivity[]>([]);
  const [profileActivityError, setProfileActivityError] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publication, setPublication] = useState<AudiencePublication | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [courtReserveUrl, setCourtReserveUrl] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueText, setIssueText] = useState("");
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const manualCopyRef = useRef<HTMLTextAreaElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const workingRequestRef = useRef(false);
  const activeAgentRunRef = useRef<string | null>(null);
  const agentEventAbortRef = useRef<AbortController | null>(null);
  const agentCancelRequestedRef = useRef(false);
  const memberPreviewRequest = useRef(0);
  const profileRequest = useRef(0);
  const publicationKey = useRef<string | null>(null);
  const chatSessionVenue = useRef<string | null>(null);
  const profileDialogRef = useRef<HTMLDivElement | null>(null);
  const profileOpenerRef = useRef<HTMLButtonElement | null>(null);
  const ready = status?.capabilities.search.allowed === true;
  const profileGroups = useMemo(() => {
    const groups = new Map<string, AudienceVisitorProfile["attributes"]>();
    for (const attribute of profile?.attributes || []) {
      const category = attribute.category || "Visitor";
      groups.set(category, [...(groups.get(category) || []), attribute]);
    }
    return Array.from(groups.entries());
  }, [profile]);

  const closeVisitorProfile = useCallback(() => {
    profileRequest.current += 1;
    setProfileOpen(false);
  }, []);

  useEffect(() => () => {
    profileRequest.current += 1;
    agentEventAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    const input = composerRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(136, Math.max(42, input.scrollHeight))}px`;
  }, [composer, loading, ready]);

  const openVisitorProfile = useCallback(async (
    visitor: AudienceVisitor,
    opener?: HTMLButtonElement,
  ) => {
    const request = ++profileRequest.current;
    if (opener) profileOpenerRef.current = opener;
    setProfileVisitor(visitor);
    setProfileOpen(true);
    setProfile(null);
    setProfileActivity([]);
    setProfileActivityError(null);
    setProfileError(null);
    setProfileLoading(true);
    try {
      const [profileResult, activityResult] = await Promise.allSettled([
        api.getAudienceVisitor(visitor.id),
        api.getAudienceVisitorActivity(visitor.id),
      ]);
      if (request !== profileRequest.current) return;
      if (profileResult.status === "rejected") throw profileResult.reason;
      setProfile(profileResult.value.member);
      if (activityResult.status === "fulfilled") {
        setProfileActivity(activityResult.value.activities || []);
      } else {
        setProfileActivityError("Historical activity could not be loaded.");
      }
    } catch (requestError) {
      if (request !== profileRequest.current) return;
      setProfileError(requestError instanceof Error ? requestError.message : "Visitor details could not be loaded.");
    } finally {
      if (request === profileRequest.current) setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!profileOpen) return;
    const dialog = profileDialogRef.current;
    const focusable = () => Array.from(dialog?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ) || []);
    window.requestAnimationFrame(() => focusable()[0]?.focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeVisitorProfile();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    const opener = profileOpenerRef.current;
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      opener?.focus();
    };
  }, [closeVisitorProfile, profileOpen]);
  const venueId = status?.venue.id || null;
  const activeConversation = useMemo(
    () =>
      conversations.find((item) => item.id === activeConversationId) || null,
    [activeConversationId, conversations],
  );
  const activeDraft = activeConversation?.draft || null;
  const activePreview =
    memberPreview?.conversationId === activeConversationId &&
    memberPreview.filterKey === JSON.stringify(activeDraft?.filter || EMPTY_FILTER)
      ? memberPreview
      : null;
  const submitIssue = async (event: FormEvent) => {
    event.preventDefault();
    setIssueSubmitting(true);
    setIssueError(null);
    const lastPrompt = [...(activeConversation?.messages || [])]
      .reverse()
      .find((message) => message.role === "user")?.text;
    try {
      await api.reportAudienceIssue({
        issue: issueText.trim() || undefined,
        context: {
          page: `${window.location.pathname}${window.location.search}`,
          clientTimestamp: new Date().toISOString(),
          conversationId: activeConversation?.id,
          conversationTitle: activeConversation?.title,
          audienceId: activeDraft?.savedAudienceId || undefined,
          audienceName: activeDraft?.name,
          lastPrompt,
        },
      });
      setIssueOpen(false);
      setIssueText("");
      await showAlert(
        "Issue reported",
        "Thanks — the PickleCourts team received your report.",
        "success",
      );
    } catch (requestError) {
      setIssueError(
        requestError instanceof Error
          ? requestError.message
          : "The issue report could not be sent.",
      );
    } finally {
      setIssueSubmitting(false);
    }
  };
  const updateActiveConversation = useCallback(
    (updater: (conversation: AudienceConversation) => AudienceConversation) => {
      setConversations((current) =>
        current.map((item) =>
          item.id === activeConversationId ? updater(item) : item,
        ),
      );
    },
    [activeConversationId],
  );
  const addMessage = useCallback(
    (message: Omit<AudienceChatMessage, "id" | "createdAt">) => {
      updateActiveConversation((conversation) =>
        updatedConversation(conversation, {
          title:
            conversation.messages.length || message.role !== "user"
              ? conversation.title
              : scrubText(message.text).slice(0, 52) || "New audience",
          messages: [
            ...conversation.messages,
            { ...message, id: makeId(), createdAt: new Date().toISOString() },
          ].slice(-MAX_MESSAGES),
        }),
      );
    },
    [updateActiveConversation],
  );
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const bootstrap: AudienceBootstrap = await api.getAudienceBootstrap();
      setStatus(bootstrap.status);
      setAttributes(
        (bootstrap.attributes || [])
          .map(normalizeAttribute)
          .filter((item) => item.filterEnabled !== false),
      );
      setRelations(bootstrap.relations || []);
      setCoverage(bootstrap.coverage || null);
      setSaved((bootstrap.audiences || []).map(normalizeSaved));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to load audiences.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!venueId) return;
    try {
      localStorage.removeItem(`pickleCourts.audienceChats.v1:${venueId}`);
      localStorage.removeItem(`pickleCourts.audienceChats.v2:${venueId}`);
    } catch {}
    let restored: AudienceConversation[] = [];
    let restoredActiveId: string | null = null;
    try {
      const raw = sessionStorage.getItem(`pickleCourts.audienceChats.v3:${venueId}`);
      const parsed = raw ? JSON.parse(raw) : null;
      restored = Array.isArray(parsed?.conversations)
        ? parsed.conversations
            .filter((item: AudienceConversation) => item?.venueId === venueId && !item.isSavedView)
            .slice(0, MAX_CONVERSATIONS)
        : [];
      restoredActiveId = typeof parsed?.activeConversationId === "string"
        ? parsed.activeConversationId
        : null;
    } catch {}
    const next = restored.length ? restored : [initialConversation(venueId)];
    setConversations(next);
    setActiveConversationId(
      next.some((item) => item.id === restoredActiveId)
        ? restoredActiveId
        : next[0].id,
    );
    chatSessionVenue.current = venueId;
  }, [venueId]);
  // Opened from the Revenue page: a weak block arrives as a ready-to-send
  // prompt in a fresh chat, a question typed there is asked straight away, and
  // a saved list opens directly. Runs once, after the restored chats are in
  // place so it is not overwritten by them.
  const [deepLink] = useState(() => readAudiencesLink(window.location.search));
  const deepLinkHandled = useRef(false);
  const deepLinkSendPending = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || working) return;
    if (!deepLink.listId && !deepLink.prompt) return;
    if (!ready || !venueId || chatSessionVenue.current !== venueId) return;
    deepLinkHandled.current = true;
    if (deepLink.listId) {
      const list = saved.find((item) => item.id === deepLink.listId);
      if (list) chooseSavedAudience(list);
      return;
    }
    createConversation();
    setComposer(deepLink.prompt || "");
    deepLinkSendPending.current = deepLink.send;
  });
  useEffect(() => {
    if (!venueId || chatSessionVenue.current !== venueId) return;
    try {
      sessionStorage.setItem(
        `pickleCourts.audienceChats.v3:${venueId}`,
        JSON.stringify({
          activeConversationId,
          conversations: conversations
            .filter((item) => !item.isSavedView)
            .slice(0, MAX_CONVERSATIONS),
        }),
      );
    } catch {}
  }, [activeConversationId, conversations, venueId]);
  useEffect(() => {
    if (
      !status?.activeRun ||
      !["queued", "running"].includes(status.activeRun.status)
    )
      return;
    const timer = window.setInterval(async () => {
      try {
        const next = await api.getAudienceBuilderStatus();
        setStatus(next);
        if (!ready && next.capabilities.search.allowed) void load();
      } catch {}
    }, SYNC_POLL_MS);
    return () => window.clearInterval(timer);
  }, [load, ready, status?.activeRun?.id, status?.activeRun?.status]);
  useEffect(() => {
    if (!publication || !["queued", "processing"].includes(publication.status))
      return;
    const timer = window.setInterval(async () => {
      try {
        setPublication(
          (await api.getAudiencePublication(publication.id)).publication,
        );
      } catch {}
    }, PUBLICATION_POLL_MS);
    return () => window.clearInterval(timer);
  }, [publication]);
  useEffect(() => {
    if (manualCopyText) {
      manualCopyRef.current?.focus();
      manualCopyRef.current?.select();
    }
  }, [manualCopyText]);
  const clearTransientContext = () => {
    setComposer("");
    setPublication(null);
    setCopyMessage(null);
    setManualCopyText(null);
    memberPreviewRequest.current += 1;
    setMemberPreview(null);
  };
  const createConversation = () => {
    if (!venueId || working) return;
    if (
      activeConversation &&
      !activeConversation.isSavedView &&
      activeConversation.messages.length === 0
    ) {
      clearTransientContext();
      return;
    }
    const next = initialConversation(venueId);
    setConversations((current) =>
      [next, ...current.filter((item) => !item.isSavedView)].slice(
        0,
        MAX_CONVERSATIONS,
      ),
    );
    setActiveConversationId(next.id);
    clearTransientContext();
  };
  const clearChatHistory = async () => {
    if (!venueId || working) return;
    const hasHistory = conversations.some(isPersistableConversation);
    if (!hasHistory) return;
    const confirmed = await showConfirm(
      "Clear recent chats?",
      "This clears chat history from this browser. Saved audiences are not affected.",
      { confirmLabel: "Clear chats", cancelLabel: "Keep chats" },
    );
    if (!confirmed) return;
    try {
      sessionStorage.removeItem(`pickleCourts.audienceChats.v3:${venueId}`);
    } catch {}
    if (activeConversation?.isSavedView) {
      setConversations([activeConversation]);
    } else {
      const next = initialConversation(venueId);
      setConversations([next]);
      setActiveConversationId(next.id);
      clearTransientContext();
    }
  };
  const loadConversationPreview = useCallback((
    conversationId: string,
    draft: AudienceDraft,
  ) => {
    if (!ready) return;
    const filterKey = JSON.stringify(draft.filter);
    const request = ++memberPreviewRequest.current;
    setMemberPreview({
      conversationId,
      filterKey,
      members: [],
      pagination: { page: 1, totalPages: 1 },
      unknownCount: 0,
      loading: true,
      error: null,
    });
    void api.searchAudienceVisitors({
      query: "",
      filter: draft.filter,
      page: 1,
      limit: 10,
    }).then((result) => {
      if (request !== memberPreviewRequest.current) return;
      setConversations((current) => current.map((conversation) =>
        conversation.id === conversationId
          ? {
              ...conversation,
              draft: conversation.draft
                ? {
                    ...conversation.draft,
                    matchedCount: result.summary?.matched ?? conversation.draft.matchedCount,
                    eligibleCount: result.summary?.eligibleProfiles ?? conversation.draft.eligibleCount,
                    sourceUpdatedAt: result.sourceUpdatedAt || conversation.draft.sourceUpdatedAt,
                  }
                : null,
              messages: conversation.messages.map((message) => message.draft
                ? {
                    ...message,
                    draft: {
                      ...message.draft,
                      matchedCount: result.summary?.matched ?? message.draft.matchedCount,
                      eligibleCount: result.summary?.eligibleProfiles ?? message.draft.eligibleCount,
                      sourceUpdatedAt: result.sourceUpdatedAt || message.draft.sourceUpdatedAt,
                    },
                  }
                : message),
            }
          : conversation,
      ));
      setMemberPreview({
        conversationId,
        filterKey,
        members: result.members || [],
        pagination: {
          page: result.pagination?.page || 1,
          totalPages: result.pagination?.totalPages || 1,
        },
        unknownCount: result.summary?.unknownCount || 0,
        loading: false,
        error: null,
      });
    }).catch(() => {
      if (request !== memberPreviewRequest.current) return;
      setMemberPreview((current) => current?.conversationId === conversationId
        ? { ...current, loading: false, error: "Could not load matching visitors." }
        : current);
    });
  }, [ready]);
  const chooseConversation = (id: string) => {
    if (working) return;
    const conversation = conversations.find((item) => item.id === id);
    setActiveConversationId(id);
    clearTransientContext();
    if (conversation?.draft) loadConversationPreview(id, conversation.draft);
  };
  const chooseSavedAudience = (audience: SavedAudience) => {
    if (!venueId || working) return;
    const draft: AudienceDraft = {
      name: audience.name,
      sourcePrompt: audience.sourcePrompt || audience.name,
      filter: audience.filterDefinition,
      interpretation: audienceDefinition(
        audience.description?.split(/\r?\n/),
        audience.sourcePrompt,
      ),
      warnings: [],
      matchedCount: audience.lastEstimatedCount || 0,
      eligibleCount: status?.data.memberCount || 0,
      savedAudienceId: audience.id,
      updatedAt: audience.updatedAt,
      requiresSave: false,
    };
    const conversation = initialConversation(venueId);
    conversation.title = audience.name;
    conversation.isSavedView = true;
    conversation.draft = draft;
    conversation.messages = [
      {
        id: makeId(),
        role: "assistant",
        kind: "audience_saved",
        text: "",
        createdAt: new Date().toISOString(),
        draft,
      },
    ];
    clearTransientContext();
    setConversations((current) =>
      [
        conversation,
        ...current.filter((item) => !item.isSavedView),
      ].slice(0, MAX_CONVERSATIONS),
    );
    setActiveConversationId(conversation.id);
    loadConversationPreview(conversation.id, draft);
  };
  const setWorkingAcknowledgement = (text: string) => {
    updateActiveConversation((conversation) =>
      updatedConversation(conversation, {
        messages: conversation.messages.map((item) =>
          item.kind === "working" ? { ...item, text } : item,
        ),
      }),
    );
  };
  const waitForInterpretation = async (
    prompt: string,
    currentFilter: AudienceFilter,
    conversation: AgentConversationMessage[],
  ): Promise<NonNullable<AudienceInterpretationJob["result"]>> => {
    const startedAt = Date.now();
    const idempotencyKey = `audience-${makeId()}`;
    const audienceContext = activeDraft
      ? {
          name: activeDraft.name,
          savedAudienceId: activeDraft.savedAudienceId,
          hasActiveDraft: true,
        }
      : null;

    // The v2 surface streams durable events. Keeping the legacy branch lets a
    // rolling portal deployment work against the one-release compatibility API.
    if (typeof (api as any).startAudienceAgentRun === "function") {
      if (!activeConversationId) throw new Error("Audience conversation is unavailable");
      const accepted = await api.startAudienceAgentRun({
          text: prompt,
          conversationId: activeConversationId,
          currentFilter,
          conversation,
          audienceContext,
          idempotencyKey,
        });
      let run = accepted.run;
      if (accepted.acknowledgement) setWorkingAcknowledgement(accepted.acknowledgement);
      activeAgentRunRef.current = run.id;
      agentCancelRequestedRef.current = false;
      let lastSequence = run.eventSequence || 0;
      let consecutivePollFailures = 0;
      if (run.status === "queued" || run.status === "running") {
        const controller = new AbortController();
        agentEventAbortRef.current = controller;
        const streamTimeout = window.setTimeout(
          () => controller.abort(),
          Math.max(1, INTERPRETATION_CLIENT_TIMEOUT_MS - (Date.now() - startedAt)),
        );
        try {
          lastSequence = await api.streamAudienceAgentRunEvents(run.id, {
            afterSequence: lastSequence,
            signal: controller.signal,
            onEvent: () => {},
          });
          run = (await api.getAudienceAgentRun(run.id)).run;
        } catch (_streamError) {
          // Polling below is the terminal-state authority.
        } finally {
          window.clearTimeout(streamTimeout);
          if (agentEventAbortRef.current === controller)
            agentEventAbortRef.current = null;
        }
      }

      // Polling is both the streaming fallback and the terminal-state authority.
      while (run.status === "queued" || run.status === "running") {
        if (Date.now() - startedAt > INTERPRETATION_CLIENT_TIMEOUT_MS)
          throw new Error("Audience agent timed out");
        await wait(INTERPRETATION_POLL_MS);
        try {
          run = (await api.getAudienceAgentRun(run.id)).run;
          lastSequence = Math.max(lastSequence, run.eventSequence || 0);
          consecutivePollFailures = 0;
        } catch (error) {
          consecutivePollFailures += 1;
          if (consecutivePollFailures >= MAX_CONSECUTIVE_POLL_FAILURES)
            throw error;
        }
      }
      activeAgentRunRef.current = null;
      if (run.status === "canceled") {
        const canceled = new Error("Canceled");
        canceled.name = "AudienceAgentCanceled";
        throw canceled;
      }
      if (run.status === "failed")
        throw new Error(run.error?.message || "Audience agent failed");
      if (!run.result) throw new Error("Audience agent returned no result");
      return interpretationFromAgentResult(run.result, currentFilter);
    }

    let job = (
      await api.startAudienceInterpretationJob(
        prompt,
        currentFilter,
        idempotencyKey,
        conversation,
        audienceContext,
      )
    ).job;
    let consecutivePollFailures = 0;

    while (job.status === "queued" || job.status === "running") {
      if (Date.now() - startedAt > INTERPRETATION_CLIENT_TIMEOUT_MS)
        throw new Error("Audience interpretation timed out");
      await wait(INTERPRETATION_POLL_MS);
      try {
        job = (await api.getAudienceInterpretationJob(job.id)).job;
        consecutivePollFailures = 0;
      } catch (error) {
        consecutivePollFailures += 1;
        if (consecutivePollFailures >= MAX_CONSECUTIVE_POLL_FAILURES)
          throw error;
      }
    }

    if (job.status === "failed")
      throw new Error(job.error?.message || "Audience interpretation failed");
    if (!job.result) throw new Error("Audience interpretation returned no result");
    return job.result;
  };
  const cancelActiveAgentRun = async () => {
    const runId = activeAgentRunRef.current;
    if (!runId || agentCancelRequestedRef.current) return;
    agentCancelRequestedRef.current = true;
    agentEventAbortRef.current?.abort();
    try {
      await api.cancelAudienceAgentRun(runId);
    } catch {
      // Polling remains authoritative and will either observe cancellation or
      // surface the completed turn if the cancel request lost the race.
    }
  };
  const loadMemberPreview = async (page = 1, refreshSaved = false) => {
    if (!activeDraft || !activeConversationId || !ready) return;
    const filterKey = JSON.stringify(activeDraft.filter);
    const request = ++memberPreviewRequest.current;
    setMemberPreview((current) =>
      current?.conversationId === activeConversationId &&
      current.filterKey === filterKey
        ? { ...current, loading: true }
        : {
            conversationId: activeConversationId,
            filterKey,
            members: [],
            pagination: { page, totalPages: 1 },
            unknownCount: 0,
            loading: true,
            error: null,
          },
    );
    try {
      const result =
        refreshSaved &&
        activeDraft.savedAudienceId &&
        !activeDraft.requiresSave
          ? await api.previewSavedAudience(activeDraft.savedAudienceId, {
              page,
              limit: 10,
            })
          : await api.searchAudienceVisitors({
              query: "",
              filter: activeDraft.filter,
              page,
              limit: 10,
            });
      if (request !== memberPreviewRequest.current) return;
      const refreshedResultAudience =
        "audience" in result ? result.audience : null;
      if (refreshedResultAudience) {
        const refreshedAudience = normalizeSaved(refreshedResultAudience);
        setSaved((current) =>
          current.map((item) =>
            item.id === refreshedAudience.id ? refreshedAudience : item,
          ),
        );
        updateActiveConversation((conversation) =>
          updatedConversation(conversation, {
            draft: conversation.draft
              ? {
                  ...conversation.draft,
                  matchedCount:
                    refreshedAudience.lastEstimatedCount ??
                    result.pagination?.total ??
                    conversation.draft.matchedCount,
                  updatedAt:
                    refreshedAudience.updatedAt || conversation.draft.updatedAt,
                }
              : null,
            messages: conversation.messages.map((message) =>
              message.draft
                ? {
                    ...message,
                    draft: {
                      ...message.draft,
                      matchedCount:
                        refreshedAudience.lastEstimatedCount ??
                        result.pagination?.total ??
                        message.draft.matchedCount,
                      updatedAt:
                        refreshedAudience.updatedAt || message.draft.updatedAt,
                    },
                  }
                : message,
            ),
          }),
        );
      } else if (page === 1) {
        updateActiveConversation((conversation) =>
          updatedConversation(conversation, {
            draft: conversation.draft
              ? {
                  ...conversation.draft,
                  matchedCount:
                    result.summary?.matched ?? conversation.draft.matchedCount,
                  eligibleCount:
                    result.summary?.eligibleProfiles ??
                    conversation.draft.eligibleCount,
                }
              : null,
            messages: conversation.messages.map((message) =>
              message.draft
                ? {
                    ...message,
                    draft: {
                      ...message.draft,
                      matchedCount:
                        result.summary?.matched ?? message.draft.matchedCount,
                      eligibleCount:
                        result.summary?.eligibleProfiles ??
                        message.draft.eligibleCount,
                    },
                  }
                : message,
            ),
          }),
        );
      }
      setMemberPreview({
        conversationId: activeConversationId,
        filterKey,
        members: result.members || [],
        pagination: {
          page: result.pagination?.page || page,
          totalPages: result.pagination?.totalPages || 1,
        },
        unknownCount: result.summary?.unknownCount || 0,
        loading: false,
        error: null,
      });
    } catch {
      if (request === memberPreviewRequest.current)
        setMemberPreview((current) =>
          current
            ? {
                ...current,
                loading: false,
                error: "Could not refresh matching visitors.",
              }
            : current,
        );
    }
  };
  const buildAudience = async (
    prompt: string,
    currentFilter: AudienceFilter,
    refinement = false,
    priorConversation = conversationHistory(activeConversation?.messages || []),
  ) => {
    if (!ready || workingRequestRef.current || !prompt.trim()) return;
    workingRequestRef.current = true;
    setError(null);
    setWorking(true);
    updateActiveConversation((conversation) => updatedConversation(conversation, {
      messages: [
        ...conversation.messages.filter((message) => !["working", "error"].includes(message.kind)),
        {
          id: makeId(), role: "assistant" as const, kind: "working" as const,
          text: "I’m checking this against your synchronized audience data. I’ll return the result here.",
          createdAt: new Date().toISOString(),
        },
      ].slice(-MAX_MESSAGES),
    }));
    try {
      const interpreted = await waitForInterpretation(
        prompt,
        currentFilter,
        priorConversation,
      );
      const filter = interpreted.filter;
      const unchanged =
        semanticFilterKey(filter) === semanticFilterKey(currentFilter);
      const action =
        interpreted.action || (unchanged ? "answer" : "audience");
      if (action === "answer" || action === "clarify") {
        updateActiveConversation((conversation) =>
          updatedConversation(conversation, {
            isInformational: activeDraft ? false : true,
            messages: [
              ...conversation.messages.filter((item) => item.kind !== "working"),
              {
                id: makeId(),
                role: "assistant" as const,
                kind: "text" as const,
                text:
                  interpreted.message ||
                  noChangeAnswer(interpreted.warnings, refinement),
                resultContext: interpreted.resultContext,
                createdAt: new Date().toISOString(),
              },
            ].slice(-MAX_MESSAGES),
          }),
        );
        return;
      }
      if (action === "save") {
        if (!activeDraft) {
          updateActiveConversation((conversation) =>
            updatedConversation(conversation, {
              messages: [
                ...conversation.messages.filter((item) => item.kind !== "working"),
                {
                  id: makeId(),
                  role: "assistant" as const,
                  kind: "text" as const,
                  text: interpreted.message || "There isn’t an audience to save yet. Tell me who you want to include first.",
                  createdAt: new Date().toISOString(),
                },
              ].slice(-MAX_MESSAGES),
            }),
          );
          return;
        }
        updateActiveConversation((conversation) =>
          updatedConversation(conversation, {
            messages: conversation.messages.filter(
              (item) => item.kind !== "working",
            ),
          }),
        );
        await saveDraft();
        return;
      }
      if (action === "start_over") {
        setMemberPreview(null);
        updateActiveConversation((conversation) =>
          updatedConversation(conversation, {
            draft: null,
            isInformational: false,
            messages: [
              ...conversation.messages.filter((item) => item.kind !== "working"),
              {
                id: makeId(),
                role: "assistant" as const,
                kind: "text" as const,
                text: interpreted.message || "Started a fresh audience.",
                createdAt: new Date().toISOString(),
              },
            ].slice(-MAX_MESSAGES),
          }),
        );
        return;
      }
      let preview: AudienceSearchResult | null = null;
      try {
        preview = await api.searchAudienceVisitors({
          query: "",
          filter,
          page: 1,
          limit: 10,
        });
      } catch {
        // The agent's draft is already server-authoritative. A transient
        // follow-up list fetch must not discard it or replace a successful
        // audience response with a generic error.
      }
      const draft: AudienceDraft = {
        name: refinement && activeDraft
          ? activeDraft.name
          : aiAudienceName(
              interpreted.audienceName || interpreted.interpretation?.[0],
              activeConversationId || makeId(),
            ),
        sourcePrompt: prompt,
        filter,
        interpretation: audienceDefinition(interpreted.interpretation, prompt),
        warnings: interpreted.warnings || [],
        matchedCount: preview?.summary?.matched ?? interpreted.matchedCount ?? 0,
        eligibleCount:
          preview?.summary?.eligibleProfiles ??
          interpreted.eligibleCount ??
          interpreted.matchedCount ??
          0,
        sourceUpdatedAt:
          preview?.sourceUpdatedAt ?? interpreted.sourceUpdatedAt ?? null,
        savedAudienceId: activeDraft?.savedAudienceId,
        updatedAt: activeDraft?.updatedAt,
        requiresSave: Boolean(activeDraft?.savedAudienceId),
      };
      updateActiveConversation((conversation) =>
        updatedConversation(conversation, {
          draft,
          isInformational: false,
          messages: [
            ...conversation.messages.filter(
              (item) =>
                !["working", "audience_draft", "audience_saved"].includes(
                  item.kind,
                ),
            ),
            {
              id: makeId(),
              role: "assistant" as const,
              kind: "audience_draft" as const,
              text:
                interpreted.message ||
                (refinement ? "Audience updated." : "Audience ready."),
              createdAt: new Date().toISOString(),
              draft,
            },
          ].slice(-MAX_MESSAGES),
        }),
      );
      if (activeConversationId) {
        setMemberPreview({
          conversationId: activeConversationId,
          filterKey: JSON.stringify(filter),
          members: preview?.members || [],
          pagination: {
            page: preview?.pagination?.page || 1,
            totalPages: preview?.pagination?.totalPages || 1,
          },
          unknownCount: preview?.summary?.unknownCount || 0,
          loading: false,
          error: preview ? null : "Could not refresh matching visitors.",
        });
      }
    } catch (err) {
      const canceled = err instanceof Error && err.name === "AudienceAgentCanceled";
      updateActiveConversation((conversation) =>
        updatedConversation(conversation, {
          messages: [
            ...conversation.messages.filter((item) => item.kind !== "working"),
            {
              id: makeId(),
              role: "assistant" as const,
              kind: canceled ? ("text" as const) : ("error" as const),
              text: canceled ? "Canceled." : audienceBuildError(err),
              createdAt: new Date().toISOString(),
              ...(canceled
                ? {}
                : { retry: { prompt, filter: currentFilter, refinement } }),
            },
          ].slice(-MAX_MESSAGES),
        }),
      );
    } finally {
      workingRequestRef.current = false;
      setWorking(false);
      activeAgentRunRef.current = null;
      agentEventAbortRef.current = null;
      agentCancelRequestedRef.current = false;
    }
  };
  const saveDraft = async () => {
    if (!activeDraft || saving || working) return;
    if (!status?.capabilities.save.allowed) {
      await showAlert(
        "Audience cannot be saved",
        status?.capabilities.save.message ||
          "Refresh visitor data and try again.",
      );
      return;
    }
    if (activeDraft.matchedCount < 1) {
      await showAlert(
        "No matching visitors",
        "Adjust the criteria before saving this audience.",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: activeDraft.name.trim() || "Untitled audience",
        description: audienceDefinition(
          activeDraft.interpretation,
          activeDraft.sourcePrompt,
        ).join("\n"),
        sourcePrompt: activeDraft.sourcePrompt,
        filter: activeDraft.filter,
        updatedAt: activeDraft.updatedAt,
      };
      const result = activeDraft.savedAudienceId
        ? await api.updateSavedAudience(activeDraft.savedAudienceId, payload)
        : await api.saveAudience(payload);
      const audience = normalizeSaved(result.audience);
      setSaved((current) =>
        activeDraft.savedAudienceId
          ? current.map((item) => (item.id === audience.id ? audience : item))
          : [audience, ...current],
      );
      const nextDraft = {
        ...activeDraft,
        name: audience.name,
        interpretation: audienceDefinition(
          audience.description?.split(/\r?\n/),
          activeDraft.sourcePrompt,
        ),
        savedAudienceId: audience.id,
        updatedAt: audience.updatedAt,
        requiresSave: false,
      };
      updateActiveConversation((conversation) =>
        updatedConversation(conversation, {
          draft: nextDraft,
          messages: [
            ...conversation.messages.filter(
              (item) =>
                !["audience_draft", "audience_saved"].includes(item.kind),
            ),
            {
              id: makeId(),
              role: "assistant" as const,
              kind: "audience_saved" as const,
              text: `Saved “${audience.name}”.`,
              createdAt: new Date().toISOString(),
              draft: nextDraft,
            },
          ].slice(-MAX_MESSAGES),
        }),
      );
    } catch (err) {
      await showAlert(
        "Could not save audience",
        err instanceof Error ? err.message : "Try again.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };
  const submit = async (event?: FormEvent, directText?: string) => {
    event?.preventDefault();
    const text = (directText ?? composer).trim();
    if (!text || working || workingRequestRef.current) return;
    const history = conversationHistory(activeConversation?.messages || []);
    setComposer("");
    addMessage({ role: "user", kind: "text", text });
    await buildAudience(
      text,
      activeDraft?.filter || EMPTY_FILTER,
      Boolean(activeDraft),
      history,
    );
  };
  // Second half of the deep link: once the fresh chat is active and holds the
  // question, ask it.
  useEffect(() => {
    if (!deepLinkSendPending.current || working) return;
    if (!activeConversation || activeConversation.isSavedView || activeConversation.messages.length > 0) return;
    if (composer !== deepLink.prompt) return;
    deepLinkSendPending.current = false;
    void submit(undefined, composer);
  });
  const updateDraftName = (name: string) =>
    updateActiveConversation((conversation) =>
      updatedConversation(conversation, {
        draft: conversation.draft
          ? {
              ...conversation.draft,
              name,
              requiresSave: conversation.draft.savedAudienceId
                ? true
                : conversation.draft.requiresSave,
            }
          : null,
        messages: conversation.messages.map((message) =>
          message.draft
            ? {
                ...message,
                draft: {
                  ...message.draft,
                  name,
                  requiresSave: message.draft.savedAudienceId
                    ? true
                    : message.draft.requiresSave,
                },
              }
            : message,
        ),
      }),
    );
  const copyEmails = async () => {
    if (!activeDraft || copying || working) return;
    setCopying(true);
    setCopyMessage(null);
    try {
      const result = await api.getAudienceEmails({
        filter: activeDraft.filter,
      });
      const text = (result.emails || []).filter(Boolean).join(",");
      if (!text) {
        setCopyMessage("No emails to copy.");
        return;
      }
      try {
        if (!navigator.clipboard?.writeText)
          throw new Error("Clipboard unavailable");
        await navigator.clipboard.writeText(text);
        setCopyMessage(
          `${result.count || result.emails.length} emails copied${result.truncated ? " (limited)" : ""}.`,
        );
      } catch {
        setManualCopyText(text);
        setCopyMessage(
          "Automatic copying was blocked. Select the list below and copy it.",
        );
      }
    } catch {
      setCopyMessage("Could not prepare the email list. Please try again.");
    } finally {
      setCopying(false);
    }
  };
  const deleteAudience = async () => {
    if (!activeDraft?.savedAudienceId || working) return;
    const confirmed = await showConfirm(
      "Delete audience?",
      `Delete “${activeDraft.name}”?`,
      {
        type: "danger",
        confirmLabel: "Delete audience",
        cancelLabel: "Keep audience",
      },
    );
    if (!confirmed) return;
    try {
      await api.deleteSavedAudience(activeDraft.savedAudienceId);
      setSaved((current) =>
        current.filter((item) => item.id !== activeDraft.savedAudienceId),
      );
      updateActiveConversation((conversation) =>
        updatedConversation(conversation, {
          draft: null,
          messages: [
            ...conversation.messages,
            {
              id: makeId(),
              role: "assistant",
              kind: "text",
              text: "The saved audience was deleted.",
              createdAt: new Date().toISOString(),
            },
          ],
        }),
      );
    } catch (err) {
      await showAlert(
        "Could not delete audience",
        err instanceof Error ? err.message : "Try again.",
        "error",
      );
    }
  };
  const publish = async () => {
    if (
      !activeDraft?.savedAudienceId ||
      !status?.capabilities.publish.allowed ||
      publishing ||
      working ||
      activeDraft.matchedCount < 1 ||
      activeDraft.matchedCount > COURTRESERVE_GROUP_MEMBER_CAP
    )
      return;
    const confirmed = await showConfirm(
      "Publish to CourtReserve?",
      `Publish “${activeDraft.name}” with ${activeDraft.matchedCount.toLocaleString()} matching ${visitorLabel(activeDraft.matchedCount)}?`,
      { confirmLabel: "Publish audience", cancelLabel: "Not yet" },
    );
    if (!confirmed) return;
    setPublishing(true);
    try {
      publicationKey.current ||= makeId();
      setPublication(
        (
          await api.publishAudienceToCourtReserve(activeDraft.savedAudienceId, {
            groupName: activeDraft.name.slice(0, 120),
            idempotencyKey: publicationKey.current,
          })
        ).publication,
      );
    } catch (err) {
      await showAlert(
        "Could not publish audience",
        err instanceof Error ? err.message : "Try again.",
        "error",
      );
    } finally {
      setPublishing(false);
    }
  };
  const connectCourtReserve = async (event: FormEvent) => {
    event.preventDefault();
    if (!courtReserveUrl.trim() || !adminEmail.trim() || !adminPassword) return;
    setConnecting(true);
    setError(null);
    try {
      await api.saveAudienceConnection(
        {
          courtReserveUrl: courtReserveUrl.trim(),
          adminEmail: adminEmail.trim(),
          adminPassword,
        },
        makeId(),
      );
      setAdminPassword("");
      await load();
    } catch (err) {
      setAdminPassword("");
      setError(
        err instanceof Error
          ? err.message
          : "CourtReserve access could not be validated.",
      );
    } finally {
      setConnecting(false);
    }
  };
  if (loading)
    return (
      <DashboardLayout venueSummary={status?.venue}>
        <main className="audience-page audience-page--loading">
          <span className="audience-loading-orb" />
          Opening audience workspace
        </main>
      </DashboardLayout>
    );
  return (
    <DashboardLayout venueSummary={status?.venue}>
      <main className="audience-page audience-chat-page">
        <RevenueBackBar block={deepLink.block} riskCents={deepLink.riskCents} />
        <header className="audience-chat-header">
          <div>
            <h1>PickleCourts Audiences</h1>
            <p>
              {status?.venue.name || "Venue"} ·{" "}
              {(status?.data.memberCount || 0).toLocaleString()}{" "}
              {visitorLabel(status?.data.memberCount || 0)}
            </p>
          </div>
          <button
            type="button"
            className="audience-report-issue-button"
            onClick={() => {
              setIssueError(null);
              setIssueOpen(true);
            }}
          >
            <span aria-hidden="true">?</span>
            Report an issue
          </button>
        </header>
        {error && (
          <div className="audience-alert" role="alert">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}
        {ready && status?.demo && (
          <div className="audience-alert audience-alert--warning" role="note">
            <strong>Sample players.</strong> This venue shows generated
            players for demos. Ask anything; nothing can be sent to
            CourtReserve from here.
          </div>
        )}
        {ready && ["stale", "expired"].includes(status?.data.freshness || "") && (
          <div className="audience-alert audience-alert--warning" role="status">
            Using the last successful visitor snapshot. Refresh is recommended,
            but viewing, grouping, and publishing remain available.
          </div>
        )}
        {ready && status?.activeRun && (
          <div
            className="audience-alert audience-alert--progress"
            role="status"
          >
            Refreshing visitors ·{" "}
            {formatSyncPhase(
              status.activeRun.progress?.phase || status.activeRun.status,
            )}
          </div>
        )}
        {!ready ? (
          <section className="audience-gate">
            <div className="audience-gate__icon">
              <Sparkle />
            </div>
            <h2>
              {status?.state === "not_entitled"
                ? "Audiences is not enabled"
                : status?.state?.startsWith("connection_")
                  ? "Connect CourtReserve"
                  : "Preparing your visitor data"}
            </h2>
            <p>
              {status?.reason?.message ||
                "Visitor data is not ready for this venue."}
            </p>
            {status &&
            ["connection_required", "connection_invalid"].includes(
              status.state,
            ) ? (
              <form
                className="audience-connection-form"
                onSubmit={connectCourtReserve}
                aria-busy={connecting}
              >
                <p>
                  Connect a CourtReserve admin account to import visitor data
                  and create groups.
                </p>
                <label htmlFor="audience-cr-url">CourtReserve URL</label>
                <input
                  id="audience-cr-url"
                  type="url"
                  required
                  value={courtReserveUrl}
                  onChange={(event) => setCourtReserveUrl(event.target.value)}
                />
                <label htmlFor="audience-cr-email">Admin email</label>
                <input
                  id="audience-cr-email"
                  type="email"
                  required
                  value={adminEmail}
                  onChange={(event) => setAdminEmail(event.target.value)}
                />
                <label htmlFor="audience-cr-password">Admin password</label>
                <input
                  id="audience-cr-password"
                  type="password"
                  required
                  value={adminPassword}
                  onChange={(event) => setAdminPassword(event.target.value)}
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={connecting}
                >
                  {connecting ? "Connecting…" : "Connect CourtReserve"}
                </button>
              </form>
            ) : status?.state !== "not_entitled" ? (
              <div className="audience-gate__actions">
                <p>
                  {status?.activeRun
                    ? `${formatSyncPhase(status.activeRun.progress?.phase || status.activeRun.status)}. It is safe to leave this page.`
                    : "Your initial visitor sync is scheduled automatically. This workspace unlocks after a complete successful snapshot."}
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => void load()}
                >
                  Check status
                </button>
              </div>
            ) : null}
          </section>
        ) : (
          <section className="audience-chat-layout">
            <aside
              className="audience-chat-sidebar"
              aria-label="Audience conversations"
            >
              <div className="audience-chat-sidebar__header">
                <button
                  type="button"
                  className="btn btn-primary audience-chat-new"
                  onClick={createConversation}
                  disabled={working}
                >
                  + New chat
                </button>
              </div>
              <section className="audience-chat-sidebar__section">
                <div className="audience-chat-section-heading">
                  <h2>Recent</h2>
                  {conversations.some(isPersistableConversation) && (
                    <button
                      type="button"
                      onClick={() => void clearChatHistory()}
                      disabled={working}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="audience-chat-history">
                  {conversations
                    .filter(
                      (conversation) =>
                        !conversation.isSavedView &&
                        conversation.messages.length > 0,
                    )
                    .map((conversation) => (
                    <button
                      type="button"
                      key={conversation.id}
                      className={`audience-chat-history-item ${conversation.id === activeConversationId ? "is-active" : ""}`}
                      onClick={() => chooseConversation(conversation.id)}
                      disabled={working}
                    >
                      <strong>{conversation.title}</strong>
                      <small>{shortDate(conversation.updatedAt)}</small>
                    </button>
                    ))}
                </div>
              </section>
              <section className="audience-chat-sidebar__section audience-chat-sidebar__section--saved">
                <div className="audience-chat-section-heading">
                  <h2>Your audiences</h2>
                  <span>{saved.length}</span>
                </div>
                <div className="audience-chat-history">
                  {saved.map((audience) => (
                    <button
                      type="button"
                      key={audience.id}
                      className={`audience-chat-history-item audience-chat-history-item--saved ${activeDraft?.savedAudienceId === audience.id ? "is-active" : ""}`}
                      onClick={() => chooseSavedAudience(audience)}
                      disabled={working}
                      aria-current={
                        activeDraft?.savedAudienceId === audience.id
                          ? "page"
                          : undefined
                      }
                    >
                      <strong>{audience.name}</strong>
                      <small>
                        {(audience.lastEstimatedCount ?? 0).toLocaleString()}{" "}
                        {visitorLabel(audience.lastEstimatedCount ?? 0)}
                      </small>
                    </button>
                  ))}
                  {!saved.length && (
                    <p className="audience-rail-empty">
                      Saved audiences will appear here.
                    </p>
                  )}
                </div>
              </section>
              <footer className="audience-chat-sidebar__status">
                <time
                  dateTime={status?.data.lastSuccessfulAt || undefined}
                  title={status?.data.lastSuccessfulAt
                    ? `Last refreshed ${new Date(status.data.lastSuccessfulAt).toLocaleString()}`
                    : "Visitor data has not been refreshed yet"}
                >
                  {lastRefreshLabel(status?.data.lastSuccessfulAt)}
                </time>
              </footer>
            </aside>
            <section
              className={`audience-chat-main ${activeDraft ? "has-preview" : ""}`}
              aria-label="Audience conversation"
            >
              <div className="audience-chat-workspace">
                <div className="audience-chat-conversation">
                  <div className="audience-chat-thread" aria-live="polite">
                {activeConversation?.messages.length ? (
                  activeConversation.messages.map((message) => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      onRetry={(message) =>
                        message.retry &&
                        void buildAudience(
                          message.retry.prompt,
                          message.retry.filter,
                          message.retry.refinement,
                        )
                      }
                      onSave={() => void saveDraft()}
                      onNameChange={updateDraftName}
                      onCopy={() => void copyEmails()}
                      onPublish={() => void publish()}
                      onDelete={() => void deleteAudience()}
                      onCancel={() => void cancelActiveAgentRun()}
                      saving={saving}
                      copying={copying}
                      publishing={publishing}
                      canSave={status?.capabilities.save.allowed === true}
                      canPublish={status?.capabilities.publish.allowed === true}
                      navigationDisabled={working}
                    />
                  ))
                ) : (
                  <Welcome
                    onPrompt={(prompt) => void submit(undefined, prompt)}
                  />
                )}
                {publication && (
                  <div
                    className={`audience-publication audience-publication--${publication.status}`}
                    role="status"
                  >
                    <strong>
                      {["succeeded", "succeeded_with_warnings"].includes(
                        publication.status,
                      )
                        ? "Published to CourtReserve"
                        : publication.status === "failed"
                          ? "Publication needs attention"
                          : "Publishing to CourtReserve"}
                    </strong>
                    <small>
                      {publication.lastError ||
                        `${(publication.intendedMemberCount ?? publication.matchedMemberCount).toLocaleString()} intended visitors`}
                    </small>
                  </div>
                )}
                {copyMessage && (
                  <p className="audience-chat-copy-status" role="status">
                    {copyMessage}
                  </p>
                )}
                {manualCopyText && (
                  <div className="audience-manual-copy">
                    <label htmlFor="audience-manual-copy-emails">
                      Emails ready to copy
                    </label>
                    <textarea
                      id="audience-manual-copy-emails"
                      ref={manualCopyRef}
                      readOnly
                      value={manualCopyText}
                      onFocus={(event) => event.currentTarget.select()}
                    />
                    <button
                      type="button"
                      onClick={() => setManualCopyText(null)}
                    >
                      Close
                    </button>
                  </div>
                )}
                  </div>
                  <form
                    className="audience-chat-composer"
                    onSubmit={(event) => void submit(event)}
                  >
                <label className="sr-only" htmlFor="audience-chat-input">
                  Describe an audience
                </label>
                <textarea
                  id="audience-chat-input"
                  className="audience-chat-composer__input"
                  value={composer}
                  ref={composerRef}
                  onChange={(event) => setComposer(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      !event.shiftKey &&
                      !(event.nativeEvent as KeyboardEvent).isComposing
                    ) {
                      event.preventDefault();
                      event.currentTarget.form?.requestSubmit();
                    }
                  }}
                  placeholder="Describe an audience or ask about your visitor data"
                  rows={1}
                  disabled={working}
                />
                <button
                  type="submit"
                  className="audience-chat-composer__send"
                  disabled={!composer.trim() || working}
                >
                  {working ? "Working…" : "Send"}
                </button>
                  </form>
                </div>
                {activeDraft && (
                  <AudienceMemberPreview
                    draft={activeDraft}
                    preview={activePreview}
                    onRefresh={() => void loadMemberPreview(1, true)}
                    onPrevious={() =>
                      void loadMemberPreview(
                        Math.max(1, (activePreview?.pagination.page || 1) - 1),
                      )
                    }
                    onNext={() =>
                      void loadMemberPreview(
                        (activePreview?.pagination.page || 1) + 1,
                      )
                    }
                    onViewVisitor={(visitor, opener) =>
                      void openVisitorProfile(visitor, opener)
                    }
                  />
                )}
              </div>
            </section>
          </section>
        )}
        {profileOpen && (
          <div
            className="audience-profile-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeVisitorProfile();
            }}
          >
            <div
              ref={profileDialogRef}
              className="audience-profile-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="audience-profile-title"
            >
              <header className="audience-profile-header">
                <div className="audience-profile-identity">
                  <span className="audience-profile-avatar" aria-hidden="true">
                    {initials(profile?.displayName || profileVisitor?.displayName || "Visitor")}
                  </span>
                  <div>
                    <span className="audience-kicker">Visitor profile</span>
                    <h2 id="audience-profile-title">
                      {profile?.displayName || profileVisitor?.displayName || "Visitor"}
                    </h2>
                    <p>
                      {profile?.profileAsOf
                        ? `Profile current as of ${new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(profile.profileAsOf))}`
                        : "Synced from CourtReserve"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="audience-profile-close"
                  aria-label="Close visitor profile"
                  onClick={closeVisitorProfile}
                >
                  ×
                </button>
              </header>

              {profileLoading ? (
                <div className="audience-profile-state" role="status">
                  <span className="audience-loading-orb" />
                  <strong>Loading visitor details…</strong>
                  <span>Gathering the latest synced profile attributes.</span>
                </div>
              ) : profileError ? (
                <div className="audience-profile-state audience-profile-state--error" role="alert">
                  <strong>Visitor details could not be loaded</strong>
                  <span>{profileError}</span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => profileVisitor && void openVisitorProfile(profileVisitor)}
                  >
                    Try again
                  </button>
                </div>
              ) : profile ? (
                <div className="audience-profile-body">
                  <section className="audience-profile-contact" aria-label="Contact details">
                    <div>
                      <span>Email</span>
                      <strong>{profile.email || "Not available"}</strong>
                    </div>
                    <div>
                      <span>Phone</span>
                      <strong>{profile.phone || "Not available"}</strong>
                    </div>
                    <div>
                      <span>Status</span>
                      <strong>{profile.recordStatus || "Active"}</strong>
                    </div>
                  </section>

                  <section className="audience-profile-activity" aria-label="Customer activity and transactions">
                    <div className="audience-profile-section-heading">
                      <h3>Activity & transactions</h3>
                      <span>{profileActivity.length}</span>
                    </div>
                    {profileActivity.length ? (
                      <ol>
                        {profileActivity.map((activity) => (
                          <li key={`${activity.sourceEntity}-${activity.sourceEntityId}-${activity.occurredAt}`}>
                            <div>
                              <strong>{activity.title}</strong>
                              <span>{activity.summary || activity.sourceEntity.replaceAll("_", " ")}</span>
                              <time dateTime={activity.occurredAt}>
                                {new Intl.DateTimeFormat("en-US", {
                                  month: "short", day: "numeric", year: "numeric",
                                  hour: "numeric", minute: "2-digit",
                                }).format(new Date(activity.occurredAt))}
                              </time>
                            </div>
                            {activity.amountCents !== null && activity.amountCents !== undefined && (
                              <b>{new Intl.NumberFormat("en-US", {
                                style: "currency", currency: "USD",
                              }).format(activity.amountCents / 100)}</b>
                            )}
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="audience-profile-empty">
                        {profileActivityError || "No synchronized historical activity is available for this visitor."}
                      </p>
                    )}
                  </section>

                  {profileGroups.length ? (
                    <div className="audience-profile-groups">
                      {profileGroups.map(([category, categoryAttributes]) => (
                        <section className="audience-profile-group" key={category}>
                          <h3>{category}</h3>
                          <dl>
                            {categoryAttributes.map((attribute) => (
                              <div key={attribute.attributePath}>
                                <dt>{attribute.displayName}</dt>
                                <dd>{formatVisitorAttribute(attribute.value, attribute.dataType, attribute.unit)}</dd>
                              </div>
                            ))}
                          </dl>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <p className="audience-profile-empty">No additional profile attributes are available for this visitor.</p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
        {issueOpen && (
          <div
            className="audience-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !issueSubmitting) {
                setIssueOpen(false);
              }
            }}
          >
            <form
              className="audience-save-modal audience-issue-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="audience-issue-title"
              onSubmit={submitIssue}
            >
              <span className="audience-kicker">Audience Builder feedback</span>
              <h2 id="audience-issue-title">Report an issue</h2>
              <p>
                We’ll include your venue and current Audience Builder context so
                the PickleCourts team can investigate.
              </p>
              <label htmlFor="audience-issue-text">
                What happened? <span>Optional</span>
              </label>
              <textarea
                id="audience-issue-text"
                autoFocus
                maxLength={1000}
                rows={5}
                value={issueText}
                onChange={(event) => setIssueText(event.target.value)}
                placeholder="Share any details that would help us reproduce the issue…"
              />
              <small>{issueText.length}/1000</small>
              {issueError && <div className="audience-issue-error" role="alert">{issueError}</div>}
              <div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={issueSubmitting}
                  onClick={() => setIssueOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={issueSubmitting}>
                  {issueSubmitting ? "Sending…" : "Send report"}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </DashboardLayout>
  );
}
function Welcome({
  onPrompt,
}: {
  onPrompt: (prompt: string) => void;
}) {
  const prompts = [
    "Help me retain members with upcoming cancellation dates.",
    "Find event attendees who aren’t members yet and are strong membership prospects.",
  ];
  return (
    <div className="audience-chat-welcome">
      <div className="audience-chat-message audience-chat-message--assistant">
        <div className="audience-chat-message__content">
          <strong>Who should we reach next?</strong>
          <p>Tell me the outcome. I’ll find the right audience, refine it with you, and explain every filter.</p>
        </div>
      </div>
      <div className="audience-chat-starters">
        {prompts.map((prompt) => (
          <button type="button" key={prompt} onClick={() => onPrompt(prompt)}>
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}
function ChatMessage({
  message,
  onRetry,
  onSave,
  onNameChange,
  onCopy,
  onPublish,
  onDelete,
  onCancel,
  saving,
  copying,
  publishing,
  canSave,
  canPublish,
  navigationDisabled,
}: {
  message: AudienceChatMessage;
  onRetry: (message: AudienceChatMessage) => void;
  onSave: () => void;
  onNameChange: (name: string) => void;
  onCopy: () => void;
  onPublish: () => void;
  onDelete: () => void;
  onCancel: () => void;
  saving: boolean;
  copying: boolean;
  publishing: boolean;
  canSave: boolean;
  canPublish: boolean;
  navigationDisabled: boolean;
}) {
  const draft = message.draft;
  if (message.kind === "working")
    return (
      <article
        className="audience-chat-message audience-chat-message--working"
        role="status"
      >
        <div className="audience-chat-message__content">
          <p>{message.text}</p>
          <div className="audience-chat-background-action">
            <span>Working in the background</span>
            <button
              type="button"
              className="audience-chat-progress__cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </article>
    );
  return (
    <article
      className={`audience-chat-message audience-chat-message--${message.role} audience-chat-message--${message.kind}`}
    >
      <div className="audience-chat-message__content">
        {message.role === "assistant" ? <AudienceChatAnswer text={message.text} /> : message.text
          .split("\n")
          .filter(Boolean)
          .map((line, index) => (
            <p key={`${message.id}-${index}`}>{line}</p>
          ))}
        {message.kind === "error" && message.retry && (
          <button
            type="button"
            className="btn btn-secondary audience-chat-retry"
            onClick={() => onRetry(message)}
            disabled={navigationDisabled}
          >
            Retry
          </button>
        )}
        {draft && (
          <AudienceResult
            draft={draft}
            onSave={onSave}
            onNameChange={onNameChange}
            onCopy={onCopy}
            onPublish={onPublish}
            onDelete={onDelete}
            saving={saving}
            copying={copying}
            publishing={publishing}
            canSave={canSave}
            canPublish={canPublish}
            navigationDisabled={navigationDisabled}
          />
        )}
      </div>
    </article>
  );
}
function AudienceResult({
  draft,
  onSave,
  onNameChange,
  onCopy,
  onPublish,
  onDelete,
  saving,
  copying,
  publishing,
  canSave,
  canPublish,
  navigationDisabled,
}: {
  draft: AudienceDraft;
  onSave: () => void;
  onNameChange: (name: string) => void;
  onCopy: () => void;
  onPublish: () => void;
  onDelete: () => void;
  saving: boolean;
  copying: boolean;
  publishing: boolean;
  canSave: boolean;
  canPublish: boolean;
  navigationDisabled: boolean;
}) {
  const publishable = Boolean(
    draft.savedAudienceId &&
    !draft.requiresSave &&
    canPublish &&
    draft.matchedCount > 0 &&
    draft.matchedCount <= COURTRESERVE_GROUP_MEMBER_CAP,
  );
  return (
    <section className="audience-chat-result">
      <header className="audience-chat-result__header">
        <div className="audience-chat-result__identity">
          <label>
            <span>Audience name</span>
            <input
              className="audience-chat-name-input"
              aria-label="Audience name"
              value={draft.name}
              onChange={(event) => onNameChange(event.target.value)}
            />
          </label>
        </div>
        <div className="audience-chat-result__metrics">
          <strong>
            {draft.matchedCount.toLocaleString()} exact{" "}
            {visitorLabel(draft.matchedCount)}
          </strong>
          <small>
            of {draft.eligibleCount.toLocaleString()} eligible{" "}
            {visitorLabel(draft.eligibleCount)}
          </small>
        </div>
      </header>
      <div className="audience-chat-result__summary" aria-label="Audience definition">
        <h3>Audience definition:</h3>
        <ul>
          {audienceDefinition(draft.interpretation, draft.sourcePrompt).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <ExactFilter filter={draft.filter} />
      </div>
      {draft.warnings.length > 0 && (
        <div className="audience-chat-result__warnings">
          {draft.warnings.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>
      )}
      <div className="audience-chat-result__actions">
        {(!draft.savedAudienceId || draft.requiresSave) && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              navigationDisabled || !canSave || saving || draft.matchedCount < 1
            }
            onClick={onSave}
          >
            {saving
              ? "Saving…"
              : draft.savedAudienceId
                ? "Save changes"
                : "Save audience"}
          </button>
        )}
        {draft.savedAudienceId && (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={navigationDisabled || copying}
              onClick={onCopy}
            >
              {copying ? "Copying…" : "Copy emails"}
            </button>
            <button
              type="button"
              className="audience-delete"
              onClick={onDelete}
              disabled={navigationDisabled}
            >
              Delete audience
            </button>
            <button
              type="button"
              className="btn btn-primary audience-chat-publish-action"
              disabled={navigationDisabled || !publishable || publishing}
              title={
                draft.requiresSave
                  ? "Save changes before publishing"
                  : !canPublish
                    ? "Fresh visitor data is required to publish"
                    : undefined
              }
              onClick={onPublish}
            >
              {publishing ? "Publishing…" : "Publish to CourtReserve"}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function ExactFilter({ filter }: { filter: AudienceFilter }) {
  const json = useMemo(() => JSON.stringify(filter, null, 2), [filter]);
  return (
    <details className="audience-exact-filter">
      <summary>Exact filter JSON</summary>
      <pre>{json}</pre>
    </details>
  );
}

function AudienceMemberPreview({
  draft,
  preview,
  onRefresh,
  onPrevious,
  onNext,
  onViewVisitor,
}: {
  draft: AudienceDraft;
  preview: MemberPreview | null;
  onRefresh: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onViewVisitor: (visitor: AudienceVisitor, opener: HTMLButtonElement) => void;
}) {
  return (
    <aside
      className="audience-panel audience-results-panel audience-chat-preview"
      aria-label="Matching visitor preview"
    >
      <header className="audience-preview-heading">
        <div>
          <span className="audience-kicker">Live preview</span>
          <h2>
            {draft.matchedCount.toLocaleString()} matching{" "}
            {visitorLabel(draft.matchedCount)}
          </h2>
          <p>
            Exact results from {draft.eligibleCount.toLocaleString()} eligible{" "}
            {visitorLabel(draft.eligibleCount)}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary audience-preview-refresh"
          onClick={onRefresh}
          disabled={preview?.loading}
          aria-label="Refresh audience preview"
        >
          {preview?.loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <div
        className={`audience-preview-body ${preview?.loading ? "is-updating" : ""}`}
      >
        {preview?.loading && (
          <div className="audience-preview-updating" role="status">
            <span className="audience-loading-orb" /> Refreshing visitors
          </div>
        )}
        <div className="audience-member-list">
          {preview?.members.length ? (
            preview.members.map((visitor) => (
              <button
                type="button"
                className="audience-member audience-member--interactive"
                key={visitor.id}
                aria-label={`View ${visitor.displayName}'s visitor profile`}
                onClick={(event) => onViewVisitor(visitor, event.currentTarget)}
              >
                <span className="audience-avatar" aria-hidden="true">
                  {initials(visitor.displayName)}
                </span>
                <div>
                  <strong>{visitor.displayName}</strong>
                  <small>
                    {visitor.email || visitor.recordStatus || "Synced visitor"}
                  </small>
                </div>
                <span className="audience-member__chevron" aria-hidden="true">›</span>
              </button>
            ))
          ) : (
            <p className="audience-empty-results">
              {preview?.error ||
                (preview?.loading
                  ? "Loading matching visitors…"
                  : "No matching visitors to preview.")}
            </p>
          )}
        </div>
      </div>

      <footer className="audience-pagination">
        <span>
          {preview?.unknownCount
            ? `${preview.unknownCount.toLocaleString()} need more data`
            : "Exact matches"}
        </span>
        <div>
          <button
            type="button"
            aria-label="Previous preview page"
            onClick={onPrevious}
            disabled={!preview || preview.loading || preview.pagination.page <= 1}
          >
            ‹
          </button>
          <span>
            {preview
              ? `${preview.pagination.page} / ${preview.pagination.totalPages}`
              : "1 / 1"}
          </span>
          <button
            type="button"
            aria-label="Next preview page"
            onClick={onNext}
            disabled={
              !preview ||
              preview.loading ||
              preview.pagination.page >= preview.pagination.totalPages
            }
          >
            ›
          </button>
        </div>
      </footer>
    </aside>
  );
}
